import React, { useMemo, useState } from "react";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Shield,
  UserCircle,
} from "lucide-react";
import { useAuthMe } from "../../hooks/useAuthMe";
import { authPostRequest } from "../../api/authClient";

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0].slice(0, 1) + parts[parts.length - 1].slice(0, 1)).toUpperCase();
}

function scorePassword(pw: string) {
  let s = 0;
  if (!pw) return 0;
  if (pw.length >= 8) s += 1;
  if (pw.length >= 12) s += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s += 1;
  if (/\d/.test(pw)) s += 1;
  if (/[^a-zA-Z0-9]/.test(pw)) s += 1;
  return Math.min(s, 5);
}

export default function Settings() {
  const { data: me, isLoading } = useAuthMe();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const fullName = useMemo(() => {
    if (!me) return "";
    return String(me.full_name ?? me.name ?? me.email ?? "");
  }, [me]);

  const email = (me?.email as string) || "";
  const clientId = String(me?.client_id ?? me?.clientId ?? me?.id ?? "");
  const initials = getInitials(fullName || email || "User");
  const pwScore = scorePassword(newPassword);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    if (!currentPassword || !newPassword) {
      setError("Please enter current password and new password.");
      return;
    }
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirm password do not match.");
      return;
    }

    setSaving(true);
    try {
      await authPostRequest("/client/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setMessage("Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.data?.message ||
        err?.message;
      setError(String(apiMsg || "Failed to change password."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800/70 dark:bg-[#0B0F19]">
        <div className="pointer-events-none absolute inset-0 opacity-60">
          <div className="absolute -left-24 -top-28 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="absolute -right-24 -bottom-28 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl" />
        </div>
        <div className="relative flex items-start justify-between gap-6 p-6 sm:p-8">
          <div className="flex min-w-0 items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900 text-white dark:bg-white dark:text-slate-900">
              <UserCircle className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                Settings
              </h1>
              <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                Manage your account and security
              </p>
            </div>
          </div>

          <div className="hidden items-center gap-2 text-xs text-slate-500 dark:text-slate-400 sm:flex">
            <Shield className="h-4 w-4" />
            Your password is never shown
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800/70 dark:bg-[#0B0F19]">
          <div className="flex items-center justify-between gap-4 border-b border-slate-200 p-6 dark:border-slate-800/70">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
                <span className="text-sm font-semibold">{initials}</span>
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Profile
                </h2>
                <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                  Logged in account details
                </p>
              </div>
            </div>
            {isLoading ? (
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600 dark:bg-[#1C202F]/55 dark:text-slate-300">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs text-emerald-700 dark:bg-emerald-950/25 dark:text-emerald-200">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Active session
              </div>
            )}
          </div>

          <dl className="divide-y divide-slate-200 text-sm dark:divide-slate-800/70">
            <div className="grid grid-cols-1 gap-1 px-6 py-4 sm:grid-cols-3 sm:gap-4">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Name
              </dt>
              <dd className="sm:col-span-2">
                <span className="block truncate font-medium text-slate-900 dark:text-slate-100">
                  {isLoading ? "—" : fullName || "—"}
                </span>
              </dd>
            </div>
            <div className="grid grid-cols-1 gap-1 px-6 py-4 sm:grid-cols-3 sm:gap-4">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Email
              </dt>
              <dd className="sm:col-span-2">
                <span className="block truncate font-medium text-slate-900 dark:text-slate-100">
                  {isLoading ? "—" : email || "—"}
                </span>
              </dd>
            </div>
            <div className="grid grid-cols-1 gap-1 px-6 py-4 sm:grid-cols-3 sm:gap-4">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Client ID
              </dt>
              <dd className="sm:col-span-2">
                <span className="block truncate font-mono text-[13px] text-slate-900 dark:text-slate-100">
                  {isLoading ? "—" : clientId || "—"}
                </span>
              </dd>
            </div>
          </dl>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800/70 dark:bg-[#0B0F19]">
          <div className="border-b border-slate-200 p-6 dark:border-slate-800/70">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700 dark:bg-[#1C202F]/55 dark:text-slate-200">
                <KeyRound className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Change password
                </h2>
                <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                  Use a strong password with 8+ characters
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4 p-6">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                Current password
              </label>
              <div className="relative">
                <input
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-sm text-slate-900 shadow-sm outline-none ring-0 transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/15 dark:border-slate-800/70 dark:bg-[#0B0F19] dark:text-slate-100"
                  placeholder="Enter current password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#1C202F]/55 dark:hover:text-slate-200"
                  aria-label={showCurrent ? "Hide current password" : "Show current password"}
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                New password
              </label>
              <div className="relative">
                <input
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-sm text-slate-900 shadow-sm outline-none ring-0 transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/15 dark:border-slate-800/70 dark:bg-[#0B0F19] dark:text-slate-100"
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#1C202F]/55 dark:hover:text-slate-200"
                  aria-label={showNew ? "Hide new password" : "Show new password"}
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              <div className="mt-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    Strength
                  </span>
                  <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
                    {pwScore <= 1
                      ? "Weak"
                      : pwScore === 2
                        ? "Fair"
                        : pwScore === 3
                          ? "Good"
                          : pwScore === 4
                            ? "Strong"
                            : "Very strong"}
                  </span>
                </div>
                <div className="mt-1 grid grid-cols-5 gap-1">
                  {Array.from({ length: 5 }).map((_, idx) => {
                    const on = idx + 1 <= pwScore;
                    const color =
                      pwScore <= 1
                        ? "bg-red-500"
                        : pwScore === 2
                          ? "bg-amber-500"
                          : pwScore === 3
                            ? "bg-yellow-500"
                            : "bg-emerald-500";
                    return (
                      <div
                        key={idx}
                        className={[
                          "h-1.5 rounded-full",
                          on ? color : "bg-slate-200 dark:bg-slate-800/70",
                        ].join(" ")}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                Confirm new password
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-sm text-slate-900 shadow-sm outline-none ring-0 transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/15 dark:border-slate-800/70 dark:bg-[#0B0F19] dark:text-slate-100"
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#1C202F]/55 dark:hover:text-slate-200"
                  aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                {error}
              </div>
            )}
            {message && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update password"
              )}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}

